import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, firstValueFrom, Observable, tap, throwError } from 'rxjs';
import { PartnerTypeEnum } from 'src/app/domain/enums/PartnerTypeEnum';
import { IContractGeneration } from 'src/app/domain/registration/models/IContractGeneration';
import { IContractSignature } from 'src/app/domain/registration/models/IContractSignature';
import { IContractTemplate } from 'src/app/domain/registration/models/IContractTemplate';
import { IPartner } from 'src/app/domain/registration/models/IPartner';
import { IPartnerSystem } from 'src/app/domain/registration/models/IPartnerSystem';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { API_PATH } from 'src/environments/environment';
import { PartnerService } from '../crud-service';
import { ContractGenerationService } from '../../contracts/services/contract-generation.service';

export interface ContractProcessingData {
    partner: IPartner;
    system?: IPartnerSystem;
    contractType?: string;
    [key: string]: any;
}

@Injectable({
    providedIn: 'root'
})
export class PartnerContractService implements IContractGeneration {
    private readonly apiUrl = API_PATH;
    private readonly monthNames = [
        'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];

    constructor(
        private partnerService: PartnerService,
        private contractGenerationService: ContractGenerationService,
        private toastService: ToastService,
        private datePipe: DatePipe,
        private http: HttpClient
    ) { }

    getPartnerContractTemplates(): Observable<Record<string, IContractTemplate>> {
        return this.contractGenerationService.getPartnerContractTemplates();
    }

    savePartnerContractTemplate(formData: FormData): Observable<any> {
        return this.contractGenerationService.savePartnerContractTemplate(formData);
    }

    updatePartnerContractTemplate(id: string, formData: FormData): Observable<any> {
        return this.http.put(`${this.apiUrl}contracts/partner-templates/${id}`, formData);
    }

    async generatePartnerContracts(
        partnerId: string,
        systemId: string,
        systemName: string,
        signatures: Array<{ name: string, email: string, type: number | string }>,
        contractType?: string
    ): Promise<any> {
        try {
            const partner = await firstValueFrom(this.partnerService.getPartnerById(partnerId));

            if (!partner) {
                throw new Error('Parceiro não encontrado');
            }

            const contractTypes = contractType ? [contractType] : this.getPartnerContractTypes(partner);

            if (contractTypes.length === 0) {
                throw new Error('O parceiro não possui papéis atribuídos para gerar contratos');
            }

            const contractData = {
                partnerId,
                systemId,
                entityType: 'partner',
                contractType: contractTypes[0],
                signatures,
                useZapSign: true
            };

            return await this.generateContract(contractData);
        } catch (error) {
            this.toastService.showError('Erro ao gerar contratos.');
            throw error;
        }
    }

    private getPartnerContractTypes(partner: IPartner): string[] {
        const types: string[] = [];

        if (partner.isConsultant) types.push(PartnerTypeEnum.CONSULTANT);
        if (partner.isRepresentative) types.push(PartnerTypeEnum.REPRESENTATIVE);
        if (partner.isCounter) types.push(PartnerTypeEnum.COUNTER);
        if (partner.isPartner) types.push(PartnerTypeEnum.PARTNER);

        return types;
    }

    async generatePreview(data: ContractProcessingData): Promise<any> {
        try {
            const templateType = this.getTemplateTypeFromContractType(data.contractType || '');
            const templates = await firstValueFrom(this.getPartnerContractTemplates()) as Record<string, any>;
            const templateKey = `${templateType}Template`;

            if (templates && templates[templateKey]) {
                const content = templates[templateKey].content;
                const processedTemplate = this.processTemplate(content, data);

                return {
                    content: processedTemplate,
                    success: true
                };
            }

            return this.fallbackGeneratePreview(data);
        } catch (error) {
            return this.fallbackGeneratePreview(data);
        }
    }

    private async fallbackGeneratePreview(data: ContractProcessingData): Promise<any> {
        try {
            const templateType = this.getTemplateTypeFromContractType(data.contractType || '');
            const templatesResponse = await firstValueFrom(this.partnerService.getContractTemplate());

            let template = '';

            const templateMap: Record<string, any> = {
                'consultant': templatesResponse.consultantTemplate,
                'partner': templatesResponse.partnerTemplate,
                'representative': templatesResponse.representativeTemplate,
                'counter': templatesResponse.counterTemplate
            };

            template = templateMap[templateType]?.content ||
                templatesResponse.defaultTemplate?.content ||
                this.getGenericTemplate(templateType);

            if (!template || template.trim().length === 0) {
                template = this.getGenericTemplate(templateType);
            }

            const processedTemplate = this.processTemplate(template, data);

            return {
                content: processedTemplate,
                success: true
            };
        } catch (error) {
            throw error;
        }
    }

    async generatePreviewWithDirectPdf(data: ContractProcessingData): Promise<Blob> {
        try {
            const templateType = this.getTemplateTypeFromContractType(data.contractType || '');
            let htmlContent = '';

            try {
                const templates = await firstValueFrom(this.getPartnerContractTemplates()) as Record<string, any>;
                const templateKey = `${templateType}Template`;

                if (templates && templates[templateKey]) {
                    const template = templates[templateKey].content;
                    htmlContent = this.processTemplate(template, data);
                } else {
                    const result = await this.fallbackGeneratePreview(data);
                    htmlContent = result.content;
                }
            } catch (error) {
                const result = await this.fallbackGeneratePreview(data);
                htmlContent = result.content;
            }

            const partnerTypeDisplay = this.getPartnerTypeDisplayName(data.contractType || '');
            const fullHtmlTemplate = this.buildHtmlTemplate(htmlContent, partnerTypeDisplay);

            const requestData = {
                customerId: "partner-preview-" + data.partner.id,
                partnerId: data.partner.id,
                systemId: data.system?.id,
                entityType: 'partner',
                contractType: templateType,
                htmlContent: fullHtmlTemplate
            };

            try {
                const pdfBlob = await firstValueFrom(
                    this.contractGenerationService.previewWithTemporaryTemplate(requestData, fullHtmlTemplate)
                );

                if (!pdfBlob || pdfBlob.size < 100) {
                    throw new Error('PDF gerado está vazio ou muito pequeno');
                }

                return pdfBlob;
            } catch (previewError) {
                const fallbackBlob = await firstValueFrom(
                    this.contractGenerationService.previewWithEditedHtml(requestData, fullHtmlTemplate)
                );

                return fallbackBlob;
            }
        } catch (error) {
            throw error;
        }
    }

    async generateContract(data: any): Promise<any> {
        try {
            return await firstValueFrom(this.contractGenerationService.generateContract(data));
        } catch (error) {
            throw error;
        }
    }

    processTemplate(template: string, data: ContractProcessingData): string {
        const partner = data.partner;
        const system = data.system;
        const currentDate = new Date();

        let processed = template;

        processed = processed.replace(/{nome_parceiro}/g, partner.name || '');
        processed = processed.replace(/{documento_parceiro}/g, partner.cnpj || partner.document || '');
        processed = processed.replace(/{email_parceiro}/g, partner.email || '');
        processed = processed.replace(/{telefone_parceiro}/g, partner.phone || partner.cellPhone || '');
        processed = processed.replace(/{endereco_parceiro}/g, this.formatAddress(partner) || '');
        processed = processed.replace(/{cidade_parceiro}/g, partner.city || '');
        processed = processed.replace(/{estado_parceiro}/g, partner.state || '');
        processed = processed.replace(/{cep_parceiro}/g, partner.zipCode || '');

        if (system) {
            processed = processed.replace(/{sistema_parceiro}/g, system.name || '');
            processed = processed.replace(/{tipo_sistema}/g, system.type === 1 ? 'Principal' : 'Módulo');
            processed = processed.replace(/{email_sistema}/g, system.email || '');
            processed = processed.replace(/{descricao_sistema}/g, system.description || '');
            processed = processed.replace(/{vencimento_sistema}/g, system.dueDate ? `Dia ${system.dueDate}` : '');
        } else {
            processed = processed.replace(/{sistema_parceiro}/g, '');
            processed = processed.replace(/{tipo_sistema}/g, '');
            processed = processed.replace(/{email_sistema}/g, '');
            processed = processed.replace(/{descricao_sistema}/g, '');
            processed = processed.replace(/{vencimento_sistema}/g, '');
        }

        processed = processed.replace(/{data_inicio}/g, this.datePipe.transform(currentDate, 'dd/MM/yyyy') || '');
        processed = processed.replace(/{data_atual}/g, this.datePipe.transform(currentDate, 'dd/MM/yyyy') || '');
        processed = processed.replace(/{dia_atual}/g, String(currentDate.getDate()).padStart(2, '0'));
        processed = processed.replace(/{mes_atual}/g, String(currentDate.getMonth() + 1).padStart(2, '0'));
        processed = processed.replace(/{ano_atual}/g, String(currentDate.getFullYear()));
        processed = processed.replace(/{mes_atual_extenso}/g, this.monthNames[currentDate.getMonth()]);

        const endDate = new Date();
        endDate.setMonth(endDate.getMonth() + 1);
        processed = processed.replace(/{data_termino}/g, this.datePipe.transform(endDate, 'dd/MM/yyyy') || '');
        processed = processed.replace(/{{([^}]+)}}/g, '{$1}');

        return processed;
    }

    private formatAddress(partner: IPartner): string {
        const parts = [];

        if (partner.address) parts.push(partner.address);
        if (partner.city) parts.push(partner.city);
        if (partner.state) parts.push(partner.state);
        if (partner.zipCode) parts.push(`CEP: ${partner.zipCode}`);

        return parts.join(', ');
    }

    private buildHtmlTemplate(content: string, partnerType: string): string {
        return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Contrato de ${partnerType}</title>
    <style>
        @page { size: A4; margin: 2cm; }
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 40px; }
        h1, h2, h3 { color: #1976d2; margin-top: 1.5em; margin-bottom: 0.8em; }
        h1 { font-size: 24px; text-align: center; border-bottom: 1px solid #1976d2; padding-bottom: 10px; }
        h2 { font-size: 20px; }
        h3 { font-size: 16px; }
        p { margin: 10px 0; text-align: justify; }
        table { width: 100%; border-collapse: collapse; margin: 20px 0; }
        table, th, td { border: 1px solid #ddd; }
        th, td { padding: 10px; text-align: left; }
        th { background-color: #f2f2f2; }
        .header { text-align: center; margin-bottom: 30px; }
        .footer { margin-top: 50px; border-top: 1px solid #ddd; padding-top: 20px; font-size: 12px; text-align: center; }
    </style>
</head>
<body>
    <div class="header">
        <h1>CONTRATO DE ${partnerType.toUpperCase()}</h1>
    </div>
    <div class="content">${content}</div>
    <div class="footer">
        <p>Documento gerado em ${new Date().toLocaleDateString('pt-BR')}</p>
    </div>
</body>
</html>`;
    }

    private getTemplateTypeFromContractType(contractType: string): string {
        if (!contractType) return 'partner';

        const typeMap: Record<string, string> = {
            [PartnerTypeEnum.CONSULTANT]: 'consultant',
            [PartnerTypeEnum.REPRESENTATIVE]: 'representative',
            [PartnerTypeEnum.COUNTER]: 'counter',
            [PartnerTypeEnum.PARTNER]: 'partner'
        };

        return typeMap[contractType] || 'partner';
    }

    private getGenericTemplate(type: string): string {
        const typeName = this.getTemplateTypeName(type);
        return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Contrato para ${typeName}</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 40px; }
        h1 { color: #1976d2; text-align: center; margin-bottom: 30px; }
        .section { margin-bottom: 20px; }
    </style>
</head>
<body>
    <h1>Contrato para ${typeName}</h1>
    <div class="section">
        <h2>Identificação das Partes</h2>
        <p><strong>Nome:</strong> {nome_parceiro}</p>
        <p><strong>Documento:</strong> {documento_parceiro}</p>
        <p><strong>Email:</strong> {email_parceiro}</p>
        <p><strong>Telefone:</strong> {telefone_parceiro}</p>
        <p><strong>Endereço:</strong> {endereco_parceiro}</p>
    </div>
    <div class="section">
        <h2>Detalhes do Contrato</h2>
        <p><strong>Data de Início:</strong> {data_inicio}</p>
        <p><strong>Data de Término:</strong> {data_termino}</p>
        <p><strong>Sistema:</strong> {sistema_parceiro}</p>
    </div>
</body>
</html>`;
    }

    private getTemplateTypeName(type: string): string {
        const typeNames: Record<string, string> = {
            'consultant': 'Consultor',
            'representative': 'Representante',
            'counter': 'Contador',
            'partner': 'Parceiro'
        };

        return typeNames[type] || 'Parceiro';
    }

    getPartnerTypeDisplayName(contractType: string): string {
        const displayNames: Record<string, string> = {
            [PartnerTypeEnum.CONSULTANT]: 'Consultor',
            [PartnerTypeEnum.REPRESENTATIVE]: 'Representante',
            [PartnerTypeEnum.COUNTER]: 'Contador',
            [PartnerTypeEnum.PARTNER]: 'Parceiro'
        };

        return displayNames[contractType] || 'Parceiro';
    }

    async getContractTemplate(type: string): Promise<string> {
        try {
            const templateType = this.getTemplateTypeFromContractType(type || '');

            try {
                const templates = await firstValueFrom(this.getPartnerContractTemplates()) as Record<string, any>;
                const templateKey = `${templateType}Template`;

                if (templates && templates[templateKey] && templates[templateKey].content) {
                    return templates[templateKey].content;
                }

                const templatesResponse = await firstValueFrom(this.partnerService.getContractTemplate());

                const templateMap: Record<string, any> = {
                    'consultant': templatesResponse.consultantTemplate,
                    'partner': templatesResponse.partnerTemplate,
                    'representative': templatesResponse.representativeTemplate,
                    'counter': templatesResponse.counterTemplate
                };

                return templateMap[templateType]?.content ||
                    templatesResponse.defaultTemplate?.content ||
                    this.getGenericTemplate(templateType);
            } catch (error) {
                return this.getGenericTemplate(templateType);
            }
        } catch (error) {
            throw error;
        }
    }

    async sendToSigningService(contractData: any): Promise<any> {
        try {
            if (contractData.useZapSign) {
                return await firstValueFrom(this.contractGenerationService.generateContract(contractData));
            }

            return contractData;
        } catch (error) {
            throw error;
        }
    }

    previewContract(previewData: {
        partnerId: string;
        contractType: string;
        systemId?: string;
        additionalSigners?: IContractSignature[]
    }): Observable<Blob> {
        return this.contractGenerationService.previewPartnerContract(previewData).pipe(
            catchError(error => {
                const errorMsg = 'Falha ao gerar prévia do contrato de parceiro.';

                if (error.error instanceof Blob) {
                    return new Observable<Blob>(observer => {
                        const reader = new FileReader();
                        reader.onload = () => {
                            try {
                                const errorResponse = JSON.parse(reader.result as string);
                                this.toastService.showError(errorResponse.message || errorMsg);
                            } catch (e) {
                                this.toastService.showError(errorMsg);
                            }
                            observer.error(new Error(errorMsg));
                        };
                        reader.onerror = () => {
                            this.toastService.showError(errorMsg);
                            observer.error(new Error(errorMsg));
                        };
                        reader.readAsText(error.error);
                    });
                }

                this.toastService.showError(error.error?.message || errorMsg);
                return throwError(() => new Error(errorMsg));
            })
        );
    }

    removeContract(contractId: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}contracts/${contractId}?contract_type=partner`);
    }
}
